import type { BoundingSphere, HierarcicalObjectReference, ObjectData, ObjectId } from "@novorender/data-js-api";
import type { ReadonlyVec3 } from "gl-matrix";

/**
 * Engine-agnostic object data access.
 *
 * Components ask this for object metadata and hierarchy instead of reaching for
 * `db` (or `view.data`, or a BIM index) directly, so they neither know nor care
 * which backend answers, or whether the answer came from the network. One
 * implementation is chosen at startup and published on explorer globals — see
 * `ObjectDbDataAccess`, `BimIndexOfflineDataAccess` and `BimIndexDataAccess`.
 *
 * Deliberately narrow: every method here is one an existing consumer needs, and
 * consumers move over to it one at a time.
 *
 * List-returning methods yield `HierarcicalObjectReference`s: id, path and node
 * type — what a list or tree draws — plus `loadMetaData()` for the one row a user
 * actually opens. That keeps listing cheap (properties for every row are not free
 * on either backend) and it is what `db.search` already yields, so object-DB
 * results pass through untouched. Richer items are still in-type: `ObjectData`
 * extends `HierarcicalObjectReference`.
 *
 * **Aborting is answered, not thrown.** A method given a signal that fires resolves
 * with its own empty value — `undefined`, `[]`, an empty `Uint32Array` — and an
 * iterator simply stops yielding; nothing here rejects with an `AbortError`. So an
 * aborted call is indistinguishable from a genuinely empty answer unless the caller
 * re-checks its own signal, which a caller that acts on the result (painting, hiding,
 * "no results") must do.
 */
export interface ObjectDataAccess {
    /**
     * Which backend this is. Diagnostics only — never branch on it.
     *
     * A BIM index says which *kind* of BIM index it is, since a downloaded one and a
     * served one differ in what they need to answer rather than in what they answer.
     */
    readonly kind: "object-db" | "bim-index-offline" | "bim-index";

    /**
     * Whether the queries that are *not* a lookup by id — property queries, name
     * search, hierarchy — can be answered with no network.
     *
     * `false` on the object DB does not mean it has no index. It answers all of those
     * perfectly well; it just asks a server to. What the `false` says is that a caller
     * which has to keep working offline cannot rely on them, and should either stay on
     * id-based access ({@link getObject}, gated on {@link isDataAvailableOffline}) or
     * tell the user the feature needs a connection.
     *
     * This and {@link isDataAvailableOffline} are the only two methods here a consumer
     * is *meant* to branch on — {@link kind} is for diagnostics and must not be.
     */
    isIndexAvailableOffline(): boolean;

    /**
     * Whether objects can be fetched by id with no network — which in practice asks
     * whether the scene carries its own metadata.
     *
     * True on either backend for a scene that does, and false on either for one that
     * does not: the BIM index holds no per-object properties, so both backends read
     * objects from the very same place. An offline BIM index therefore still needs this
     * checked before {@link getObject} is trusted offline.
     */
    isDataAvailableOffline(): boolean;

    /** Full metadata for one object, or `undefined` when it cannot be resolved. */
    getObject(id: ObjectId, signal?: AbortSignal): Promise<ObjectData | undefined>;

    /**
     * The first object at `path`, or `undefined` when there is none — how a caller
     * reaches a node it only knows by path. "First" because a path is not unique in
     * every model, the same rule the path-keyed model tree follows.
     *
     * If you already have an object and want to get its parent, use getParent method instead.
     */
    getObjectAtPath(path: string, signal?: AbortSignal): Promise<ObjectData | undefined>;

    /**
     * The object's immediate parent, or `undefined` for a root.
     *
     * Takes the object rather than an id so each backend can use whichever identity
     * it indexes by — the path it already carries, or the id — without a lookup to
     * get there. A caller should not derive a parent path itself: which of the two
     * is cheaper is exactly the engine detail this interface hides.
     */
    getParent(object: HierarcicalObjectReference, signal?: AbortSignal): Promise<ObjectData | undefined>;

    /**
     * An object's children, or the root nodes when `parent` is `undefined`.
     *
     * Paging is the implementation's business: the iterator fetches more as it is
     * consumed and a caller that stops early (`break`) stops the fetching, so no
     * cursor crosses this boundary. Keeping a partially-consumed iterator around is
     * how a lazily-expanding tree resumes — the pattern `features/modelTree` already
     * uses with `db.search`.
     */
    getChildren(
        parent: HierarcicalObjectReference | undefined,
        signal?: AbortSignal,
    ): AsyncIterableIterator<HierarcicalObjectReference>;

    /**
     * Objects matching every term of {@link PropertyQuery} — "these objects, by
     * properties whose values I know".
     *
     * Exact matching only; substring and full-text belong to {@link searchByName} and
     * nothing else, for the reason given there.
     *
     * Rows, so a caller can read a path or open metadata. A caller that only wants the
     * ids — and especially one that wants the matches' subtrees too — should ask
     * {@link findIdsByProperties} instead, which is the same question in a form the
     * backends can answer without materializing a row per match.
     */
    findByProperties(query: PropertyQuery, signal?: AbortSignal): AsyncIterableIterator<HierarcicalObjectReference>;

    /**
     * Ids of the objects matching {@link PropertyQuery}, and with
     * `includeDescendants` everything beneath the matches that are not leaves —
     * "these objects and the subtrees they stand for".
     *
     * The same question {@link findByProperties} answers, reduced to what a caller that
     * paints, hides or selects actually uses. It is a method of its own rather than a
     * loop over the iterator because the reduction is exactly what the backends can
     * optimize: an index holds the match set as a bitmap and a subtree as a bitmap, so
     * the union is one operation on data it already has, where a caller stitching it
     * together spends a row (and, with descendants, a request) per match.
     *
     * **Distinct**, which the loop it replaces was not: a backend that unions bitmaps
     * de-duplicates for free while one that concatenates subtrees does not, and a caller
     * showing "hid N objects" must not get a different N per backend. Order is
     * unspecified. Packed for {@link getDescendantIds}' reason — with
     * `includeDescendants` this is the same order of magnitude, since one broad criterion
     * can name a subtree that is most of the model.
     *
     * A subtree a backend cannot read is left out rather than failing the whole answer,
     * for {@link getDescendantIds}' reason inverted: one unreadable container out of
     * thousands should cost its own ids, not everyone's.
     */
    findIdsByProperties(query: PropertyQuery, options?: FindIdsOptions): Promise<Uint32Array>;

    /**
     * Objects whose name contains `term`, case-insensitively — what the object tree's
     * search box asks for.
     *
     * **Best-effort on either backend**, and the only method here that is. It is
     * separate from {@link findByProperties} rather than a term in its algebra because a
     * term composes: a partial answer inside a conjunction would silently narrow the
     * whole result, with nothing to say it had. As its own method the weaker guarantee
     * is something a caller opts into.
     *
     * The backends reach it very differently. The object DB asks the server for a
     * non-exact `name` pattern. A BIM index has no substring operator that scales —
     * `CONTAINS` is unaccelerated, so it degrades into a scan that times out and comes
     * back *silently partial* — and therefore approximates via the accelerated
     * full-text column, which matches tokens and prefixes and so cannot find a term
     * starting mid-token (see `BimIndexOfflineDataAccess`). Treat the result as a search box's
     * input, never as a set to compute over.
     */
    searchByName(term: string, signal?: AbortSignal): AsyncIterableIterator<HierarcicalObjectReference>;

    /**
     * The object's ancestor chain, root first, excluding the object itself.
     *
     * Ancestors a backend cannot produce are left out rather than guessed, so the
     * result may be shorter than the object's path is deep — a caller that stitches
     * a tree must cope with gaps.
     */
    getAncestors(object: HierarcicalObjectReference, signal?: AbortSignal): Promise<HierarcicalObjectReference[]>;

    /**
     * Ids of everything below the object, for the whole subtree, **excluding the
     * object itself** — a leaf therefore reports none.
     *
     * Ids rather than objects because callers use this to paint or hide, not to read
     * properties — and packed, because the set reaches hundreds of thousands on a
     * real model. Both backends produce one natively: the object DB's pre-computed
     * descendants file is packed on load, and the index materializes a bitmap.
     */
    getDescendantIds(object: HierarcicalObjectReference, signal?: AbortSignal): Promise<Uint32Array>;

    /**
     * Make the object's subtree ids available on it (`object.descendants`) *if that is
     * cheap*, and report whether they are now there.
     *
     * For background warm-ups, which want the ids only when they come for free: a
     * caller doing this for hundreds of nodes must not trigger hundreds of expensive
     * subtree walks. The object DB uses its pre-computed descendants file and gives up
     * when there isn't one, rather than falling back to a paginated search the way
     * {@link getDescendantIds} does.
     *
     * **A backend may decline entirely**, and `BimIndexOfflineDataAccess` does: cheap
     * to *compute* is not the same as cheap to *keep*, and the field this fills is a
     * plain array pinned to a reference. A `false` is therefore not "no descendants" —
     * it only means they are not on the object, and {@link getDescendantIds} is how to
     * get them.
     */
    warmDescendantIds(object: HierarcicalObjectReference, signal?: AbortSignal): Promise<boolean>;

    /**
     * The volume enclosing a set of objects.
     */
    getBounds(ids: Iterable<ObjectId>, options?: BoundsOptions): Promise<SelectionBounds | undefined>;
}

/** Which enclosing shape(s) {@link ObjectDataAccess.getBounds} should compute. */
export type BoundsParts = "box" | "sphere" | "both";

/**
 * The volume enclosing a set of objects. Only the part(s) that were asked for are
 * present; an absent one says nothing about whether the selection has that geometry.
 */
export interface SelectionBounds {
    box?: { min: ReadonlyVec3; max: ReadonlyVec3 };
    sphere?: BoundingSphere;
}

/** What {@link ObjectDataAccess.getBounds} is asked for. */
export interface BoundsOptions {
    /**
     * Defaults to `"sphere"` — every consumer in this app frames a camera, and a sphere
     * is what `renderActions.setCamera` takes. Asking for one part rather than both
     * roughly halves the work on an index backend, which probes each separately.
     */
    parts?: BoundsParts;
    signal?: AbortSignal;
}

/**
 * What {@link ObjectDataAccess.findIdsByProperties} is asked for.
 *
 * The signal rides in here rather than as a second positional the way every other
 * method takes it: this is the one method with an option of its own, and a third
 * positional reads worse at every call site than one bag does.
 */
export interface FindIdsOptions {
    /**
     * Also include everything beneath each match that is not a leaf.
     *
     * A container that matches stands for its whole subtree — which is what "similar
     * objects" means to someone hiding or isolating them. Off by default: the method
     * finds ids by properties, and the subtrees are the extra thing a caller opts into.
     */
    includeDescendants?: boolean;
    signal?: AbortSignal;
}

/**
 * What one {@link PropertyTerm} asks of its property — exactly one of these.
 *
 * The absent members are typed `never` rather than left out so that a term narrows
 * on a plain truthiness check, and so that a caller cannot pass two at once.
 */
type PropertyMatch =
    /** Holds any one of these values, compared exactly (case-insensitively). */
    | { anyOf: readonly string[]; range?: never; defined?: never }
    /** Holds a value within these inclusive bounds. */
    | { anyOf?: never; range: { min: string; max: string }; defined?: never }
    /** Holds any value at all — the property is simply present. */
    | { anyOf?: never; range?: never; defined: true };

/** One condition on one property. */
export type PropertyTerm = PropertyMatch & {
    /** Property name, or one of `id` / `path` / `name`. */
    property: string;
    /** Match objects that *fail* the condition instead. */
    negate?: boolean;
};

/**
 * A conjunction of per-property conditions.
 *
 * This is deliberately the *intersection* of what the backends can answer exactly,
 * not the union: `SearchPattern[]` on the object DB and `AND` over `IN`/`RANGE`/
 * `DEFINED` predicates on a BIM index, each a near-direct translation of a term.
 *
 * Two absences are the design:
 *
 * - **No OR across properties.** The index has an `OR`, but `SearchPattern[]` is
 *   ANDed with no way to express one, so an `or` here would be a method only some
 *   implementations could honour. OR *within* one property is `anyOf`.
 * - **No substring or full-text.** Both backends have a `CONTAINS`, but the index's
 *   does not scale and answers partially without saying so. Inexact matching lives
 *   on {@link ObjectDataAccess.searchByName} alone.
 *
 * Subtree scoping is the widening to expect next — several call sites still on `db`
 * combine `parentPath` with a `searchPattern`. Both backends can push it down (the
 * object DB as `parentPath`, the index through the engine's `filteredSubtree`), so
 * it belongs here rather than in a caller that would otherwise pull a whole subtree
 * to filter it; it waits only on the worker call and a consumer to prove it.
 */
export interface PropertyQuery {
    /** ANDed together. An empty list matches nothing rather than everything. */
    terms: readonly PropertyTerm[];
}

/** The common case: one property, one set of accepted values. */
export function byProperty(property: string, values: readonly string[]): PropertyQuery {
    return { terms: [{ property, anyOf: values }] };
}
